// Browser — дерево объектов проекта (Fusion 360 style, светлая тема).
//
// Структура:
//   ▾ 📄 [Документ] (●)
//      ▸ ⚙ Настройки документа
//      ▸ 📁 Сохранённые виды
//      ▸ 📁 Наборы выделения
//      ▸ 🌐 Начало координат
//      ▾ 📁 Тела (N)
//          • тело 1
//          • тело 2
//      ▸ 📁 Эскизы (N)
//      ▸ 📁 Вспомогательная геометрия

import React, { useState } from 'react';
import { toast } from './Toasts';
import RU from '../i18n/ru';
import useCadStore from '../store/useCadStore';
import { getGeometryClient } from '../geometryClient';
import { getBodyIndex } from '../bodyHistoryHelper';
import {
    IconTriRight, IconTriDown,
    IconEye, IconEyeHidden,
    IconFolder, IconDocument, IconActiveDot, IconGear,
    IconBodyItem, IconSketchItem, IconPlaneItem, IconAxisItem, IconOriginPoint,
    IconOrigin,
} from './icons';
import cl from './Browser.module.css';


// === Универсальный TreeNode ===
// Узел дерева с треугольником-стрелкой и иконкой в стиле Fusion.
// Для категорий (Bodies, Sketches, Origin) есть optional 👁-toggle.
const TreeNode = ({
    Icon, iconColor, label, count, defaultExpanded = true,
    children, level = 0,
    visible = null,           // null = нет глаза; true/false = есть
    onToggleVisible = null,
    activeMarker = false,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const hasChildren = !!children;
    const indent = 8 + level * 12;

    return (
        <div className={cl.node}>
            <div className={cl.row} style={{ paddingLeft: indent }}>
                <button
                    className={cl.expandBtn}
                    onClick={() => hasChildren && setExpanded(!expanded)}
                >
                    {hasChildren && (expanded ? <IconTriDown/> : <IconTriRight/>)}
                </button>

                {onToggleVisible !== null && (
                    <button
                        className={`${cl.eyeBtn} ${visible ? cl.eyeBtnVisible : cl.eyeBtnHidden}`}
                        onClick={(e) => { e.stopPropagation(); onToggleVisible(); }}
                        title={visible ? RU.common.hide : RU.common.show}
                    >
                        {visible ? <IconEye/> : <IconEyeHidden/>}
                    </button>
                )}

                {Icon && (
                    <span
                        className={cl.rowIcon}
                        style={iconColor ? { color: iconColor } : undefined}
                    >
                        <Icon/>
                    </span>
                )}

                <span className={cl.rowLabel}>{label}</span>

                {count != null && <span className={cl.rowCount}>{count}</span>}

                {activeMarker && (
                    <span className={cl.activeDot} title="Активный документ">
                        <IconActiveDot/>
                    </span>
                )}
            </div>

            {hasChildren && expanded && (
                <div className={cl.children}>{children}</div>
            )}
        </div>
    );
};


// === Запись тела в Browser ===
const BodyItemRow = ({ bodyId, label, level }) => {
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const visibility = useCadStore((s) => s.bodyVisibility);
    const toggleBodyVisible = useCadStore((s) => s.toggleBodyVisible);
    const removeBody = useCadStore((s) => s.removeBody);

    const isSelected = selection.kind === 'body' && selection.ids.includes(bodyId);
    const visible = visibility.has(bodyId) ? visibility.get(bodyId) : true;
    const [menu, setMenu] = useState(null);
    const indent = 8 + level * 12;

    const onClick = (e) => {
        e.stopPropagation();
        if (e.shiftKey && selection.kind === 'body') {
            const cur = selection.ids;
            const next = cur.includes(bodyId)
                ? cur.filter((id) => id !== bodyId)
                : [...cur, bodyId];
            setSelection(next.length ? 'body' : null, next);
        } else {
            setSelection('body', [bodyId]);
        }
    };

    const onContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu({ x: e.clientX, y: e.clientY });
    };

    const onDelete = () => {
        setMenu(null);
        if (!window.confirm(`Удалить ${label}?`)) return;
        getGeometryClient().deleteBody(bodyId);
        removeBody(bodyId);
    };

    // Объединить: если выделено 2+ тел и текущая строка — одно из них.
    // Делаем chain combine — попарно сливаем все выбранные через .fuse().
    const multiSelected = selection.kind === 'body'
        && selection.ids.length >= 2
        && selection.ids.includes(bodyId);
    const onCombine = async () => {
        setMenu(null);
        const ids = [...selection.ids];
        if (ids.length < 2) return;
        try {
            // Считаем индексы тел ДО combine (после combine они исчезнут)
            const sourceIdxs = ids.map((id) => getBodyIndex(id));
            const newId = await chainCombineBodies(ids, 'add');
            // Записываем фичу в timeline (для Undo, истории и rebuild)
            useCadStore.getState().addFeature({
                id: `feat_${Date.now().toString(36)}`,
                kind: 'combine',
                operation: 'add',
                sourceIds: ids,
                sourceIdxs,        // для rebuild
                resultId: newId,
                createdAt: Date.now(),
            });
            // Выделяем результат
            useCadStore.getState().setSelection('body', [newId]);
        } catch (e) {
            console.error('[Browser combine] failed:', e);
            toast('Ошибка объединения: ' + (e?.message || e), 'error');
        }
    };

    return (
        <div
            className={`${cl.row} ${cl.itemRow} ${isSelected ? cl.itemSelected : ''}`}
            style={{ paddingLeft: indent }}
            onClick={onClick}
            onContextMenu={onContextMenu}
            title={`${label} (${bodyId})`}
        >
            <span className={cl.expandBtn}/>{/* отступ под треугольник */}
            <button
                className={`${cl.eyeBtn} ${visible ? cl.eyeBtnVisible : cl.eyeBtnHidden}`}
                onClick={(e) => { e.stopPropagation(); toggleBodyVisible(bodyId); }}
                title={visible ? RU.common.hide : RU.common.show}
            >
                {visible ? <IconEye/> : <IconEyeHidden/>}
            </button>
            <span className={cl.rowIcon} style={{ color: '#88ccff' }}>
                <IconBodyItem/>
            </span>
            <span className={cl.rowLabel}>{label}</span>

            {menu && (
                <BrowserContextMenu
                    x={menu.x} y={menu.y}
                    onClose={() => setMenu(null)}
                    items={[
                        ...(multiSelected ? [{
                            label: `Объединить (${selection.ids.length} тел)`,
                            onClick: onCombine,
                        }] : []),
                        { label: visible ? RU.common.hide : RU.common.show,
                          onClick: () => { toggleBodyVisible(bodyId); setMenu(null); } },
                        { label: 'Удалить', onClick: onDelete, danger: true },
                    ]}
                />
            )}
        </div>
    );
};


// === Последовательное объединение нескольких тел ===
// Воркер `combineBodies` работает попарно (target+tool). Для 3+ тел делаем
// последовательно: первые два объединяем, потом результат+третье, и так далее.
// Возвращает bodyId финального результата.
async function chainCombineBodies(ids, operation = 'add') {
    if (!Array.isArray(ids) || ids.length < 2) {
        throw new Error('Нужно минимум 2 тела');
    }
    const client = getGeometryClient();
    let currentId = ids[0];
    for (let i = 1; i < ids.length; i++) {
        const result = await client.combineBodies({
            targetId: currentId,
            toolId:   ids[i],
            operation,
            keepTool: false,
        });
        // combineBodies резолвится с { bodyId: newId } — это id нового тела
        currentId = result?.bodyId || currentId;
    }
    return currentId;
}


// === Запись эскиза в Browser ===
const SketchItemRow = ({ sketch, idx, level }) => {
    const visibility = useCadStore((s) => s.sketchVisibility);
    const toggleSketchVisible = useCadStore((s) => s.toggleSketchVisible);
    const enterSketchMode = useCadStore((s) => s.enterSketchMode);
    const visible = visibility.has(sketch.id) ? visibility.get(sketch.id) : true;
    const planeName = sketch.planeRef?.plane || '';
    const label = `Эскиз ${idx + 1}${planeName ? ` (${planeName})` : ''}`;
    const indent = 8 + level * 12;

    const [menu, setMenu] = useState(null);

    const onContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu({ x: e.clientX, y: e.clientY });
    };

    return (
        <div
            className={`${cl.row} ${cl.itemRow}`}
            style={{ paddingLeft: indent }}
            onContextMenu={onContextMenu}
            onDoubleClick={() => enterSketchMode(sketch.id)}
            title={`${label} — двойной клик чтобы войти в редактирование`}
        >
            <span className={cl.expandBtn}/>
            <button
                className={`${cl.eyeBtn} ${visible ? cl.eyeBtnVisible : cl.eyeBtnHidden}`}
                onClick={(e) => { e.stopPropagation(); toggleSketchVisible(sketch.id); }}
                title={visible ? RU.common.hide : RU.common.show}
            >
                {visible ? <IconEye/> : <IconEyeHidden/>}
            </button>
            <span className={cl.rowIcon} style={{ color: '#cdd6e0' }}>
                <IconSketchItem/>
            </span>
            <span className={cl.rowLabel}>{label}</span>

            {menu && (
                <BrowserContextMenu
                    x={menu.x} y={menu.y}
                    onClose={() => setMenu(null)}
                    items={[
                        { label: 'Войти в эскиз', onClick: () => { enterSketchMode(sketch.id); setMenu(null); } },
                        { label: visible ? RU.common.hide : RU.common.show,
                          onClick: () => { toggleSketchVisible(sketch.id); setMenu(null); } },
                    ]}
                />
            )}
        </div>
    );
};


// === Запись плоскости/оси/точки origin (некликабельная пока) ===
const OriginItemRow = ({ Icon, label, color, level }) => {
    const indent = 8 + level * 12;
    return (
        <div className={`${cl.row} ${cl.itemRow}`} style={{ paddingLeft: indent }}>
            <span className={cl.expandBtn}/>
            <span className={`${cl.eyeBtn} ${cl.eyeBtnVisible}`}>
                <IconEye/>
            </span>
            <span className={cl.rowIcon} style={{ color }}>
                <Icon/>
            </span>
            <span className={cl.rowLabel}>{label}</span>
        </div>
    );
};


// === Запись рабочей плоскости со смещением ===
// Двойной клик / меню «Создать эскиз» — создаёт эскиз на этой
// плоскости и входит в режим редактирования эскиза.
const WorkplaneItemRow = ({ wp, idx, level }) => {
    const addSketch = useCadStore((s) => s.addSketch);
    const addFeature = useCadStore((s) => s.addFeature);
    const enterSketchMode = useCadStore((s) => s.enterSketchMode);
    const removeWorkplane = useCadStore((s) => s.removeWorkplane);
    const indent = 8 + level * 12;
    const label = `${wp.name || `Плоскость ${idx + 1}`}`
        + ` (${wp.basePlane} ${wp.offset >= 0 ? '+' : ''}${wp.offset})`;

    const [menu, setMenu] = useState(null);

    // Создать эскиз на рабочей плоскости — planeRef с базовой
    // плоскостью + offset (движок строит эскиз так же, как на грани).
    const createSketchHere = () => {
        const planeRef = {
            kind: 'workplane',
            workplaneId: wp.id,
            plane: wp.basePlane,
            offset: wp.offset || 0,
        };
        const sketchId = `sketch_${Date.now().toString(36)}`;
        addSketch({ id: sketchId, planeRef, primitives: [], constraints: [] });
        addFeature({
            id: `feat_${Date.now().toString(36)}`,
            kind: 'sketch', sketchId, planeRef, createdAt: Date.now(),
        });
        enterSketchMode(sketchId);
    };

    const onContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu({ x: e.clientX, y: e.clientY });
    };

    return (
        <div
            className={`${cl.row} ${cl.itemRow}`}
            style={{ paddingLeft: indent }}
            onContextMenu={onContextMenu}
            onDoubleClick={createSketchHere}
            title={`${label} — двойной клик чтобы создать эскиз на плоскости`}
        >
            <span className={cl.expandBtn}/>
            <span className={`${cl.eyeBtn} ${cl.eyeBtnVisible}`}>
                <IconEye/>
            </span>
            <span className={cl.rowIcon} style={{ color: '#e88a3a' }}>
                <IconPlaneItem/>
            </span>
            <span className={cl.rowLabel}>{label}</span>

            {menu && (
                <BrowserContextMenu
                    x={menu.x} y={menu.y}
                    onClose={() => setMenu(null)}
                    items={[
                        { label: 'Создать эскиз',
                          onClick: () => { createSketchHere(); setMenu(null); } },
                        { label: 'Удалить плоскость',
                          onClick: () => { removeWorkplane(wp.id); setMenu(null); } },
                    ]}
                />
            )}
        </div>
    );
};


// === Запись рабочей оси (через 2 точки) ===
const WorkaxisItemRow = ({ ax, idx, level }) => {
    const removeWorkaxis = useCadStore((s) => s.removeWorkaxis);
    const indent = 8 + level * 12;
    const fmt = (p) => `(${p[0]},${p[1]},${p[2]})`;
    const label = `${ax.name || `Ось ${idx + 1}`}`;

    const [menu, setMenu] = useState(null);

    const onContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu({ x: e.clientX, y: e.clientY });
    };

    return (
        <div
            className={`${cl.row} ${cl.itemRow}`}
            style={{ paddingLeft: indent }}
            onContextMenu={onContextMenu}
            title={`${label}: ${fmt(ax.p1)} → ${fmt(ax.p2)}`}
        >
            <span className={cl.expandBtn}/>
            <span className={`${cl.eyeBtn} ${cl.eyeBtnVisible}`}>
                <IconEye/>
            </span>
            <span className={cl.rowIcon} style={{ color: '#e88a3a' }}>
                <IconAxisItem/>
            </span>
            <span className={cl.rowLabel}>{label}</span>

            {menu && (
                <BrowserContextMenu
                    x={menu.x} y={menu.y}
                    onClose={() => setMenu(null)}
                    items={[
                        { label: 'Удалить ось',
                          onClick: () => { removeWorkaxis(ax.id); setMenu(null); } },
                    ]}
                />
            )}
        </div>
    );
};


/*
 * Строка механического соединения в дереве.
 *
 * Соединения ХРАНИЛИСЬ в документе, но нигде не показывались: человек ставил
 * шарнир и после закрытия панели не мог ни увидеть его, ни удалить.
 */
const JointItemRow = ({ jt, idx, level }) => {
    const removeJoint = useCadStore((s) => s.removeJoint);
    const indent = 8 + level * 12;

    const KIND_RU = {
        revolute: 'Шарнир',
        slider: 'Ползун',
        rigid: 'Жёсткое',
    };
    const label = jt.name || `${KIND_RU[jt.kind] || 'Соединение'} ${idx + 1}`;
    const unit = jt.kind === 'slider' ? 'мм' : '°';
    const limits = (jt.min !== null && jt.min !== undefined)
        || (jt.max !== null && jt.max !== undefined)
        ? `, ход ${jt.min ?? '−∞'}…${jt.max ?? '+∞'} ${unit}`
        : '';

    const [menu, setMenu] = useState(null);

    return (
        <div
            className={`${cl.row} ${cl.itemRow}`}
            style={{ paddingLeft: indent }}
            onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenu({ x: e.clientX, y: e.clientY });
            }}
            title={`${label}: ${jt.bodyA} → ${jt.bodyB}${limits}`}
        >
            <span className={cl.expandBtn}/>
            <span className={`${cl.eyeBtn} ${cl.eyeBtnVisible}`}>
                <IconEye/>
            </span>
            <span className={cl.rowIcon} style={{ color: '#e8b73a' }}>
                <IconAxisItem/>
            </span>
            <span className={cl.rowLabel}>{label}</span>

            {menu && (
                <BrowserContextMenu
                    x={menu.x} y={menu.y}
                    onClose={() => setMenu(null)}
                    items={[
                        { label: 'Удалить соединение',
                          onClick: () => { removeJoint(jt.id); setMenu(null); } },
                    ]}
                />
            )}
        </div>
    );
};


// === Контекстное меню ===
const BrowserContextMenu = ({ x, y, items, onClose }) => {
    React.useEffect(() => {
        const onDoc = () => onClose();
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        setTimeout(() => {
            window.addEventListener('mousedown', onDoc);
            window.addEventListener('keydown', onKey);
        }, 0);
        return () => {
            window.removeEventListener('mousedown', onDoc);
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    return (
        <div
            className={cl.contextMenu}
            style={{ left: x, top: y }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {items.map((it, i) => (
                <button
                    key={i}
                    className={`${cl.contextItem} ${it.danger ? cl.contextItemDanger : ''}`}
                    onClick={it.onClick}
                >
                    {it.label}
                </button>
            ))}
        </div>
    );
};


// === Главный компонент ===
const Browser = () => {
    const doc = useCadStore((s) => s.doc);
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);
    const isModified = useCadStore((s) => s.isModified);

    const docTitle = (doc.title || 'Без названия') + (isModified ? '*' : '');
    const bodyIds = [...bodyMeshes.keys()];

    return (
        <div className={cl.browser}>
            <div className={cl.header}>ОБОЗРЕВАТЕЛЬ</div>
            <div className={cl.tree}>
                {/* Главный документ */}
                <TreeNode
                    Icon={IconDocument}
                    iconColor="#88ccff"
                    label={docTitle}
                    defaultExpanded
                    activeMarker
                    level={0}
                >
                    <TreeNode
                        Icon={IconGear}
                        label="Настройки документа"
                        defaultExpanded={false}
                        level={1}
                    />
                    <TreeNode
                        Icon={IconFolder}
                        iconColor="#e8b84a"
                        label="Сохранённые виды"
                        defaultExpanded={false}
                        level={1}
                    />
                    <TreeNode
                        Icon={IconFolder}
                        iconColor="#e8b84a"
                        label="Наборы выделения"
                        defaultExpanded={false}
                        level={1}
                    />
                    <TreeNode
                        Icon={IconOrigin}
                        iconColor="#d88a8a"
                        label="Начало координат"
                        defaultExpanded={false}
                        level={1}
                    >
                        <OriginItemRow Icon={IconPlaneItem} label="XY" color="#5a98ff" level={2}/>
                        <OriginItemRow Icon={IconPlaneItem} label="XZ" color="#3dca80" level={2}/>
                        <OriginItemRow Icon={IconPlaneItem} label="YZ" color="#ff5566" level={2}/>
                        <OriginItemRow Icon={IconAxisItem} label="X" color="#ff5566" level={2}/>
                        <OriginItemRow Icon={IconAxisItem} label="Y" color="#3dca80" level={2}/>
                        <OriginItemRow Icon={IconAxisItem} label="Z" color="#5a98ff" level={2}/>
                        <OriginItemRow Icon={IconOriginPoint} label="Точка начала" color="#a8a8a8" level={2}/>
                    </TreeNode>
                    <TreeNode
                        Icon={IconFolder}
                        iconColor="#e8b84a"
                        label="Тела"
                        count={bodyIds.length}
                        defaultExpanded
                        level={1}
                    >
                        {bodyIds.map((id, idx) => (
                            <BodyItemRow
                                key={id}
                                bodyId={id}
                                label={`Тело ${idx + 1}`}
                                level={2}
                            />
                        ))}
                        {bodyIds.length === 0 && (
                            <div className={cl.empty} style={{ paddingLeft: 32 }}>(пусто)</div>
                        )}
                    </TreeNode>
                    <TreeNode
                        Icon={IconFolder}
                        iconColor="#e8b84a"
                        label="Эскизы"
                        count={doc.sketches.length}
                        defaultExpanded={false}
                        level={1}
                    >
                        {doc.sketches.map((sk, idx) => (
                            <SketchItemRow key={sk.id} sketch={sk} idx={idx} level={2}/>
                        ))}
                        {doc.sketches.length === 0 && (
                            <div className={cl.empty} style={{ paddingLeft: 32 }}>(пусто)</div>
                        )}
                    </TreeNode>
                    <TreeNode
                        Icon={IconFolder}
                        iconColor="#e8b84a"
                        label="Вспомогательная геометрия"
                        count={(doc.workplanes || []).length
                            + (doc.workaxes || []).length}
                        defaultExpanded={false}
                        level={1}
                    >
                        {(doc.workplanes || []).map((wp, idx) => (
                            <WorkplaneItemRow
                                key={wp.id} wp={wp} idx={idx} level={2}/>
                        ))}
                        {(doc.workaxes || []).map((ax, idx) => (
                            <WorkaxisItemRow
                                key={ax.id} ax={ax} idx={idx} level={2}/>
                        ))}
                        {(doc.workplanes || []).length === 0
                          && (doc.workaxes || []).length === 0 && (
                            <div className={cl.empty} style={{ paddingLeft: 32 }}>(пусто)</div>
                        )}
                    </TreeNode>
                    {/* Соединения показываем отдельным разделом — их не
                        спутать со вспомогательной геометрией: это связи
                        между телами, а не построительные объекты. */}
                    <TreeNode
                        Icon={IconFolder}
                        iconColor="#e8b84a"
                        label="Соединения"
                        count={(doc.joints || []).length}
                        defaultExpanded={false}
                        level={1}
                    >
                        {(doc.joints || []).map((jt, idx) => (
                            <JointItemRow key={jt.id} jt={jt} idx={idx} level={2}/>
                        ))}
                        {(doc.joints || []).length === 0 && (
                            <div className={cl.empty} style={{ paddingLeft: 32 }}>(пусто)</div>
                        )}
                    </TreeNode>
                </TreeNode>
            </div>
        </div>
    );
};

export default Browser;
export { BrowserContextMenu };
